/**
 * Organization Monetization Dashboard
 * 
 * A motivating dashboard showing organizations how they earn through CamPassed.
 * Designed to make organizations feel rewarded for using the platform.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
  DollarSign, 
  TrendingUp, 
  Eye, 
  MousePointer,
  Users,
  Megaphone,
  Gift,
  ArrowRight,
  Sparkles,
  Clock,
  CheckCircle,
  ChevronRight,
  Info,
  CreditCard,
  BarChart3,
  Target,
  Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function Monetization() {
  const { session, organization } = useAuth();
  const [loading, setLoading] = useState(true);
  const [earnings, setEarnings] = useState(null);
  const [splitInfo, setSplitInfo] = useState(null);
  const [sponsors, setSponsors] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [period, setPeriod] = useState(30);

  useEffect(() => {
    fetchData();
  }, [session?.access_token, organization?.id, period]);

  const fetchData = async () => {
    if (!session?.access_token || !organization?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Fetch organization earnings
      const earningsRes = await fetch(
        `${API_URL}/api/revenue/organizations/${organization.id}/earnings?days=${period}`,
        { headers: { Authorization: `Bearer ${session.access_token}` } }
      );
      if (earningsRes.ok) {
        setEarnings(await earningsRes.json());
      }

      // Fetch split info
      const splitRes = await fetch(`${API_URL}/api/revenue/split-info`);
      if (splitRes.ok) {
        setSplitInfo(await splitRes.json());
      }

      // Fetch sponsors (mock for now until sponsors API is connected)
      setSponsors([
        { id: '1', name: 'Red Bull', tier: 'gold', status: 'active', revenue: 245.50 },
        { id: '2', name: 'Spotify', tier: 'silver', status: 'active', revenue: 128.00 },
        { id: '3', name: 'Local Pizza Co', tier: 'bronze', status: 'active', revenue: 45.00 }
      ]);

      // Fetch campaigns (mock)
      setCampaigns([
        { id: '1', name: 'Spring Break Promo', sponsor: 'Red Bull', status: 'active', impressions: 12500, clicks: 342 },
        { id: '2', name: 'Music Discovery', sponsor: 'Spotify', status: 'active', impressions: 8200, clicks: 186 },
        { id: '3', name: 'Late Night Deals', sponsor: 'Local Pizza Co', status: 'paused', impressions: 3100, clicks: 89 }
      ]);

    } catch (error) {
      console.error('Failed to fetch monetization data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Calculate derived metrics
  const adRevenue = earnings?.breakdown?.filter(b => 
    b.event_type === 'ad_impression' || b.event_type === 'ad_click'
  ).reduce((sum, b) => sum + (b.earnings || 0), 0) || 0;

  const sponsorRevenue = earnings?.breakdown?.filter(b => 
    b.event_type === 'sponsorship_fee'
  ).reduce((sum, b) => sum + (b.earnings || 0), 0) || 0;

  const totalImpressions = earnings?.breakdown?.filter(b => 
    b.event_type === 'ad_impression'
  ).reduce((sum, b) => sum + (b.event_count || 0), 0) || 0;

  const totalClicks = earnings?.breakdown?.filter(b => 
    b.event_type === 'ad_click'
  ).reduce((sum, b) => sum + (b.event_count || 0), 0) || 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-purple-500/30 border-t-purple-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/50">Loading your earnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="org-monetization-dashboard">
      {/* Header with motivation */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-500/10 via-emerald-500/10 to-blue-500/10 border border-white/[0.06] p-6">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gradient-to-br from-emerald-500/10 to-transparent rounded-full blur-3xl" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-5 h-5 text-amber-400" />
            <span className="text-sm font-medium text-amber-400">Your Revenue Dashboard</span>
          </div>
          <h1 className="text-2xl font-semibold text-white mb-2">
            Earn While You Grow
          </h1>
          <p className="text-white/60 max-w-xl">
            CamPassed is free for your organization. You earn <span className="text-emerald-400 font-medium">70%</span> of all 
            monetization revenue from ads and sponsorships attributed to your events.
          </p>
        </div>
        
        {/* Period selector */}
        <div className="absolute top-6 right-6 flex items-center gap-2 bg-white/[0.04] rounded-lg p-1">
          {[7, 30, 90].map(days => (
            <button
              key={days}
              onClick={() => setPeriod(days)}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                period === days 
                  ? 'bg-white/10 text-white' 
                  : 'text-white/50 hover:text-white'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Main earnings cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Earnings - Hero Card */}
        <div className="md:col-span-1 bg-gradient-to-br from-emerald-500/20 via-emerald-500/10 to-transparent rounded-2xl border border-emerald-500/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <DollarSign className="w-6 h-6 text-emerald-400" />
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              Your 70% Share
            </span>
          </div>
          <p className="text-4xl font-bold text-white mb-1">
            ${earnings?.total_earnings?.toFixed(2) || '0.00'}
          </p>
          <p className="text-sm text-white/50">Total Earnings</p>
          
          <div className="mt-4 pt-4 border-t border-emerald-500/10 grid grid-cols-2 gap-3">
            <div>
              <p className="text-lg font-semibold text-amber-400">${earnings?.pending_earnings?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-white/40 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Pending
              </p>
            </div>
            <div>
              <p className="text-lg font-semibold text-white">${earnings?.paid_earnings?.toFixed(2) || '0.00'}</p>
              <p className="text-xs text-white/40 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Paid
              </p>
            </div>
          </div>
        </div>

        {/* Ad Revenue Card */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-purple-500/10">
              <Megaphone className="w-5 h-5 text-purple-400" />
            </div>
            <TrendingUp className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">${adRevenue.toFixed(2)}</p>
          <p className="text-sm text-white/50">Ad Revenue</p>
          <p className="text-xs text-white/30 mt-2">From impressions & clicks on your events</p>
        </div>

        {/* Sponsor Revenue Card */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 rounded-xl bg-amber-500/10">
              <Gift className="w-5 h-5 text-amber-400" />
            </div>
            <TrendingUp className="w-4 h-4 text-emerald-400" />
          </div>
          <p className="text-3xl font-bold text-white mb-1">${sponsorRevenue.toFixed(2)}</p>
          <p className="text-sm text-white/50">Sponsor Revenue</p>
          <p className="text-xs text-white/30 mt-2">From sponsor partnerships</p>
        </div>
      </div>

      {/* Performance metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Impressions */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-blue-500/10">
              <Eye className="w-4 h-4 text-blue-400" />
            </div>
            <span className="text-sm text-white/50">Impressions</span>
          </div>
          <p className="text-2xl font-semibold text-white">{totalImpressions.toLocaleString()}</p>
          <p className="text-xs text-white/30 mt-1">Ads shown on your events</p>
        </div>

        {/* Clicks */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-cyan-500/10">
              <MousePointer className="w-4 h-4 text-cyan-400" />
            </div>
            <span className="text-sm text-white/50">Clicks</span>
          </div>
          <p className="text-2xl font-semibold text-white">{totalClicks.toLocaleString()}</p>
          <p className="text-xs text-white/30 mt-1">Ad interactions by attendees</p>
        </div>

        {/* CTR */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-emerald-500/10">
              <Target className="w-4 h-4 text-emerald-400" />
            </div>
            <span className="text-sm text-white/50">Click Rate</span>
          </div>
          <p className="text-2xl font-semibold text-white">
            {totalImpressions > 0 ? ((totalClicks / totalImpressions) * 100).toFixed(1) : '0.0'}%
          </p>
          <p className="text-xs text-white/30 mt-1">Engagement rate</p>
        </div>

        {/* Active Sponsors */}
        <div className="bg-white/[0.02] backdrop-blur-xl rounded-xl border border-white/[0.06] p-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Users className="w-4 h-4 text-amber-400" />
            </div>
            <span className="text-sm text-white/50">Sponsors</span>
          </div>
          <p className="text-2xl font-semibold text-white">{sponsors.filter(s => s.status === 'active').length}</p>
          <p className="text-xs text-white/30 mt-1">Active partnerships</p>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - 2/3 width */}
        <div className="lg:col-span-2 space-y-6">
          {/* Active Sponsors */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] overflow-hidden">
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Gift className="w-5 h-5 text-amber-400" />
                <h3 className="font-medium text-white">Active Sponsors</h3>
              </div>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white">
                Manage <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {sponsors.length > 0 ? (
              <div className="divide-y divide-white/[0.04]">
                {sponsors.map(sponsor => (
                  <div key={sponsor.id} className="p-4 hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          sponsor.tier === 'gold' ? 'bg-amber-500/20' :
                          sponsor.tier === 'silver' ? 'bg-gray-400/20' :
                          'bg-orange-700/20'
                        }`}>
                          <span className="text-lg font-bold text-white">{sponsor.name[0]}</span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{sponsor.name}</p>
                          <p className="text-xs text-white/40 capitalize">{sponsor.tier} Tier</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-emerald-400">${sponsor.revenue.toFixed(2)}</p>
                        <p className="text-xs text-white/40">Your earnings</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Gift className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No sponsors yet</p>
                <p className="text-sm text-white/30 mt-1">Attract sponsors to earn more revenue</p>
              </div>
            )}
          </div>

          {/* Recent Campaigns */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] overflow-hidden">
            <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Megaphone className="w-5 h-5 text-purple-400" />
                <h3 className="font-medium text-white">Recent Campaigns</h3>
              </div>
              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white">
                View All <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {campaigns.length > 0 ? (
              <div className="divide-y divide-white/[0.04]">
                {campaigns.map(campaign => (
                  <div key={campaign.id} className="p-4 hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-medium text-white">{campaign.name}</p>
                        <p className="text-xs text-white/40">by {campaign.sponsor}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        campaign.status === 'active' 
                          ? 'bg-emerald-500/10 text-emerald-400' 
                          : 'bg-amber-500/10 text-amber-400'
                      }`}>
                        {campaign.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-white/50">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {campaign.impressions.toLocaleString()}
                      </span>
                      <span className="flex items-center gap-1">
                        <MousePointer className="w-3 h-3" /> {campaign.clicks.toLocaleString()}
                      </span>
                      <span className="text-emerald-400">
                        {((campaign.clicks / campaign.impressions) * 100).toFixed(1)}% CTR
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <Megaphone className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No campaigns yet</p>
              </div>
            )}
          </div>

          {/* Recent Revenue Events */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] overflow-hidden">
            <div className="p-4 border-b border-white/[0.06]">
              <div className="flex items-center gap-3">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                <h3 className="font-medium text-white">Recent Revenue Events</h3>
              </div>
            </div>
            
            {earnings?.recent_events && earnings.recent_events.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-xs text-white/40 border-b border-white/[0.04]">
                      <th className="text-left p-4 font-medium">Type</th>
                      <th className="text-right p-4 font-medium">Gross</th>
                      <th className="text-right p-4 font-medium">Your Earnings</th>
                      <th className="text-right p-4 font-medium">Status</th>
                      <th className="text-right p-4 font-medium">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/[0.04]">
                    {earnings.recent_events.slice(0, 10).map((event, i) => (
                      <tr key={event.id || i} className="hover:bg-white/[0.02] transition-colors">
                        <td className="p-4">
                          <span className="text-sm text-white">{formatEventType(event.type)}</span>
                        </td>
                        <td className="p-4 text-right text-sm text-white/50">
                          ${event.gross_amount?.toFixed(4)}
                        </td>
                        <td className="p-4 text-right text-sm text-emerald-400 font-medium">
                          ${event.earnings?.toFixed(4)}
                        </td>
                        <td className="p-4 text-right">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            event.status === 'paid' ? 'bg-emerald-500/10 text-emerald-400' :
                            event.status === 'processed' ? 'bg-blue-500/10 text-blue-400' :
                            'bg-amber-500/10 text-amber-400'
                          }`}>
                            {event.status}
                          </span>
                        </td>
                        <td className="p-4 text-right text-sm text-white/40">
                          {formatDate(event.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center">
                <BarChart3 className="w-10 h-10 text-white/20 mx-auto mb-3" />
                <p className="text-white/50">No revenue events yet</p>
                <p className="text-sm text-white/30 mt-1">Events will appear as monetization occurs</p>
              </div>
            )}
          </div>
        </div>

        {/* Right column - 1/3 width */}
        <div className="space-y-6">
          {/* Revenue Split Explanation */}
          <div className="bg-gradient-to-br from-white/[0.04] to-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-blue-500/10">
                <Info className="w-5 h-5 text-blue-400" />
              </div>
              <h3 className="font-medium text-white">How You Earn</h3>
            </div>
            
            <p className="text-sm text-white/60 mb-4">
              {splitInfo?.description || 'CamPassed shares monetization revenue with your organization.'}
            </p>
            
            {/* Visual split bar */}
            <div className="mb-4">
              <div className="flex rounded-lg overflow-hidden h-10">
                <div 
                  className="bg-emerald-500/40 flex items-center justify-center text-sm text-white font-bold"
                  style={{ width: '70%' }}
                >
                  70%
                </div>
                <div 
                  className="bg-purple-500/30 flex items-center justify-center text-sm text-white/70 font-medium"
                  style={{ width: '30%' }}
                >
                  30%
                </div>
              </div>
              <div className="flex justify-between mt-2 text-xs">
                <span className="text-emerald-400 font-medium">Your Earnings</span>
                <span className="text-white/40">Platform Fee</span>
              </div>
            </div>
            
            {/* Revenue sources */}
            <div className="space-y-2 pt-4 border-t border-white/[0.06]">
              <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Revenue Sources</p>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Zap className="w-3 h-3 text-amber-400" />
                <span>Ad Impressions & Clicks</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Gift className="w-3 h-3 text-amber-400" />
                <span>Sponsor Partnerships</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/70">
                <Target className="w-3 h-3 text-amber-400" />
                <span>Promoted Listings</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-white/50">
                <span className="w-3 h-3 flex items-center justify-center text-xs">🎫</span>
                <span>Ticket Fees (coming soon)</span>
              </div>
            </div>
          </div>

          {/* Payout Section */}
          <div className="bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/[0.06] p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CreditCard className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-medium text-white">Payouts</h3>
            </div>
            
            <div className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.04] mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-white/50">Available for payout</span>
                <span className="text-lg font-semibold text-emerald-400">
                  ${earnings?.pending_earnings?.toFixed(2) || '0.00'}
                </span>
              </div>
              <div className="w-full bg-white/[0.06] rounded-full h-2">
                <div 
                  className="bg-emerald-500/50 h-2 rounded-full transition-all"
                  style={{ width: `${Math.min((earnings?.pending_earnings || 0) / 50 * 100, 100)}%` }}
                />
              </div>
              <p className="text-xs text-white/40 mt-2">
                Minimum payout: $50.00
              </p>
            </div>
            
            <div className="space-y-2 text-sm">
              <div className="flex items-center justify-between text-white/50">
                <span>Payout schedule</span>
                <span className="text-white">Monthly</span>
              </div>
              <div className="flex items-center justify-between text-white/50">
                <span>Payment method</span>
                <span className="text-white/70">Not connected</span>
              </div>
            </div>
            
            <Button 
              className="w-full mt-4 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400 border border-emerald-500/20"
              disabled
            >
              <CreditCard className="w-4 h-4 mr-2" />
              Connect Payout Method
            </Button>
            <p className="text-xs text-white/30 text-center mt-2">Coming soon with Stripe Connect</p>
          </div>

          {/* Motivation Card */}
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-2xl border border-amber-500/20 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <h3 className="font-medium text-white">Grow Your Earnings</h3>
            </div>
            <p className="text-sm text-white/60 mb-4">
              The more events you host and the more engagement you drive, the more you earn!
            </p>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>Host more events to increase ad impressions</span>
              </li>
              <li className="flex items-start gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>Partner with sponsors for guaranteed revenue</span>
              </li>
              <li className="flex items-start gap-2 text-white/70">
                <CheckCircle className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>Promote events to drive more attendance</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

// Helper functions
function formatEventType(type) {
  const labels = {
    'ad_impression': 'Ad Impression',
    'ad_click': 'Ad Click',
    'sponsorship_fee': 'Sponsorship',
    'ticket_fee': 'Ticket Fee',
    'promoted_listing': 'Promoted',
    'other': 'Other'
  };
  return labels[type] || type;
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}
